import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { makeStyles, AppBar, Toolbar } from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';

import IconButton from 'components/IconButton';
import { MenuBar, IconTooltip } from '../';
import { STATIC_PATH } from 'constants';

const drawerWidth = 240;

const useStyles = makeStyles((theme) => {
  return {
    root: {
      borderBottom: 'solid 1px #E5E5E5',
      boxShadow: 'none',
      position: 'absolute',
      top: ({ alertBannerHeight }) => `${alertBannerHeight}px`,
      // topBar has elevation 2 on sizes smaller than 'xs'
      [theme.breakpoints.down('xs')]: {
        borderBottom: 'none',
        boxShadow:
          '0px 3px 1px -2px rgba(0, 0, 0, 0.2), 0px 2px 2px rgba(0, 0, 0, 0.14), 0px 1px 5px rgba(0, 0, 0, 0.12)',
      },
      zIndex: 2,
      '& .MuiToolbar-root': {
        minHeight: theme.spacing(8),
        height: theme.spacing(8),
        [theme.breakpoints.down('xs')]: {
          minHeight: theme.spacing(6),
          height: theme.spacing(6),
        },
      },
      '& .MuiTypography-colorPrimary': {
        color: theme.palette.greyBlue[600], //hijacking default primary color of typography as required by design
      },
    },
    toolbar: {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'nowrap',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingRight: theme.spacing(2),
      paddingLeft: theme.spacing(1.75),
    },
    toolbarContainer: {
      display: 'flex',
      flexDirection: 'row',
      flexWrap: 'nowrap',
    },
    // side navigation drawer
    drawerOpen: {
      width: drawerWidth,
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
      }),
    },
    drawerClose: {
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      overflowX: 'hidden',
      width: theme.spacing(7) + 1,
      [theme.breakpoints.up('sm')]: {
        width: theme.spacing(9) + 1,
      },
    },
    // RedShelf logo
    logoContainer: {
      [theme.breakpoints.down('xs')]: {
        display: 'none',
      },
      display: 'flex',
      alignItems: 'center',
      marginLeft: theme.spacing(2.5),
    },
    menuDrawerButton: {
      marginLeft: 0,
    },
  };
});

const TopBar = ({
  alertBannerHeight,
  areLearningToolsOpen,
  bookLocation,
  className,
  openDrawerButtonRef,
  setAnnounceLocationChangeMessage,
  toggleNav = () => {},
  topBarRef,
  ...props
}) => {
  const classes = useStyles({ alertBannerHeight });

  return (
    <AppBar
      ref={topBarRef}
      color="inherit"
      className={clsx(classes.root, className)}
      {...props}
    >
      <Toolbar className={classes.toolbar}>
        <div className={classes.toolbarContainer}>
          <IconTooltip id="lt-drawer" title="Learning Tools Drawer">
            <IconButton
              aria-controls="nav-drawer"
              aria-expanded={areLearningToolsOpen}
              aria-label="Learning Tools Drawer"
              className={classes.menuDrawerButton}
              color="inherit"
              disableRipple={true}
              edge="start"
              onClick={toggleNav}
              ref={openDrawerButtonRef}
              tabIndex="0"
            >
              <MenuIcon />
            </IconButton>
          </IconTooltip>
          <div className={classes.logoContainer}>
            <img
              src={`${STATIC_PATH}/images/logo.svg`}
              alt="Redshelf eReader logo"
              width={268}
            />
          </div>
        </div>
        <MenuBar
          bookLocation={bookLocation}
          setAnnounceLocationChangeMessage={setAnnounceLocationChangeMessage}
        />
      </Toolbar>
    </AppBar>
  );
};

TopBar.propTypes = {
  alertBannerHeight: PropTypes.number,
  areLearningToolsOpen: PropTypes.bool,
  bookLocation: PropTypes.object,
  className: PropTypes.string,
  openDrawerButtonRef: PropTypes.object,
  setAnnounceLocationChangeMessage: PropTypes.func,
  toggleNav: PropTypes.func,
  topBarRef: PropTypes.object,
};

export default TopBar;
